import type { SupportedLocale } from '@pricedrop/i18n';
import { format as dateFnsFormat, intervalToDuration, type Locale } from 'date-fns';
import { enUS, he } from 'date-fns/locale';
export type TimeFormat = '12h' | '24h';
export type WeekStartsOn = 0 | 1 | 2 | 3 | 4 | 5 | 6;

const dateFnsLocales: Record<SupportedLocale, Locale> = {
  'en-US': enUS,
  'he-IL': he,
};

const timeFormatToHour12: Record<TimeFormat, boolean> = {
  '12h': true,
  '24h': false,
};

function getHour12Preference(locale: SupportedLocale, timeFormat?: TimeFormat): boolean {
  if (timeFormat) {
    return timeFormatToHour12[timeFormat];
  }
  return locale.startsWith('en');
}

function getHourFormat(hour12: boolean): Intl.DateTimeFormatOptions['hour'] {
  return hour12 ? 'numeric' : '2-digit';
}

export function getDateFnsLocale(locale: SupportedLocale): Locale {
  return dateFnsLocales[locale] ?? enUS;
}

export function formatDate(date: Date, formatStr: string, locale: SupportedLocale): string {
  return dateFnsFormat(date, formatStr, { locale: getDateFnsLocale(locale) });
}

export function formatTimeString(
  timeStr: string,
  locale: SupportedLocale,
  timeFormat?: TimeFormat,
): string {
  const [hours, minutes] = timeStr.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return timeStr;
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return formatTime(date, locale, timeFormat);
}

export function formatTime(date: Date, locale: SupportedLocale, timeFormat?: TimeFormat): string {
  const hour12 = getHour12Preference(locale, timeFormat);
  const options: Intl.DateTimeFormatOptions = {
    hour: getHourFormat(hour12),
    minute: '2-digit',
    hour12,
  };

  return new Date(date).toLocaleTimeString(locale, options);
}

export function formatDateTime(
  date: Date,
  locale: SupportedLocale,
  timeFormat?: TimeFormat,
): string {
  const hour12 = getHour12Preference(locale, timeFormat);
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: getHourFormat(hour12),
    minute: '2-digit',
    hour12,
  };

  return new Date(date).toLocaleString(locale, options);
}

/**
 * Format a duration between now and a future date as a human-readable string.
 * Uses the two most significant units for readability.
 * Examples: "2 hours and 30 minutes", "1 day and 3 hours", "45 minutes"
 */
export function formatTimeUntil(futureDate: Date, locale: SupportedLocale): string {
  const duration = intervalToDuration({ start: new Date(), end: futureDate });
  const days = duration.days ?? 0;
  const hours = duration.hours ?? 0;
  const minutes = duration.minutes ?? 0;

  const isHebrew = locale.startsWith('he');

  const parts: string[] = [];
  if (days > 0) {
    parts.push(
      isHebrew ? (days === 1 ? 'יום' : `${days} ימים`) : days === 1 ? '1 day' : `${days} days`,
    );
  }
  if (hours > 0) {
    parts.push(
      isHebrew
        ? hours === 1
          ? 'שעה'
          : `${hours} שעות`
        : hours === 1
          ? '1 hour'
          : `${hours} hours`,
    );
  }
  if (minutes > 0 && days === 0) {
    parts.push(
      isHebrew
        ? minutes === 1
          ? 'דקה'
          : `${minutes} דקות`
        : minutes === 1
          ? '1 minute'
          : `${minutes} minutes`,
    );
  }

  if (parts.length === 0) {
    return isHebrew ? 'פחות מדקה' : 'less than a minute';
  }

  const separator = isHebrew ? ' ו' : ' and ';
  return parts.join(separator);
}
